import React, { useState, useRef } from 'react'
import axios from 'axios';
import { baseUrl } from "../../comman/config";
import { useNavigate, Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';

// Change Password, for the signed-in user.
//
// The current password is what makes this endpoint safe: the user id it acts on comes from the
// browser, so without it anyone who could guess an id could set somebody else's password. It is
// checked against the stored hash on the server; the checks here are only to save a round trip.
export default function ChangePassword() {

    const empty = { current_password: '', new_password: '', confirm_password: '' };
    // Per field, not one switch for all three: someone checking a typo in the new password has no
    // reason to put their current one on the screen beside it.
    const [shown, setShown] = useState({ current_password: false, new_password: false, confirm_password: false });
    const toggleShown = (name) => setShown((prev) => ({ ...prev, [name]: !prev[name] }));
    const [formData, setFormData] = useState(empty);
    const [validationErrors, setValidationErrors] = useState({});
    const [message, setMessage] = useState(null);
    const [saving, setSaving] = useState(false);
    const formRef = useRef(null);

    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((previous) => ({ ...previous, [name]: value }));
        setValidationErrors((previous) => ({ ...previous, [name]: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {
            current_password: formData.current_password === '',
            new_password: formData.new_password === '',
            confirm_password: formData.confirm_password === '',
        };
        setValidationErrors(errors);
        if (Object.values(errors).some(Boolean)) {
            const first = formRef.current.querySelector('.invalid');
            if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (formData.new_password !== formData.confirm_password) {
            setMessage({ type: 'error', text: 'The new password and its confirmation do not match.' });
            return;
        }

        setMessage(null);
        setSaving(true);
        try {
            await axios.post(`${baseUrl}/user_master/change_password`, {
                user_id: Number(userId),
                formData,
            });
            setFormData(empty);
            setMessage({ type: 'success', text: 'Password changed. Use the new one next time you sign in.' });
        } catch (error) {
            console.log(error);
            setMessage({ type: 'error', text: error?.response?.data?.msg ?? 'Could not change your password.' });
        }
        setSaving(false);
    };

    const field = (name, label) => (
        <div className="sm:col-span-2">
            <label htmlFor={name} className="block text-sm font-medium leading-6 text-gray-900">{label} *</label>
            <div className="mt-2 relative">
                <input
                    type={shown[name] ? 'text' : 'password'}
                    name={name}
                    id={name}
                    placeholder={label}
                    autoComplete={name === 'current_password' ? 'current-password' : 'new-password'}
                    value={formData[name]}
                    onChange={handleInputChange}
                    className={`block w-full rounded-md border ${validationErrors[name] ? 'border-red-500 invalid' : 'border-gray-300'} py-1.5 pl-1.5 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                />
                <button
                    type="button"
                    onClick={() => toggleShown(name)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    aria-label={shown[name] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
                    tabIndex={-1}
                >
                    {shown[name]
                        ? <EyeOffIcon className="h-5 w-5 text-gray-500" />
                        : <EyeIcon className="h-5 w-5 text-gray-500" />}
                </button>
            </div>
            {validationErrors[name] && <div className="text-red-500">Please enter {label.toLowerCase()}.</div>}
        </div>
    );

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Change Password</h1>
                <Link to="/profile">
                    <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back to Profile</button>
                </Link>
            </div>
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg px-4 py-2">
                <form ref={formRef}>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
                        {field('current_password', 'Current Password')}
                        {field('new_password', 'New Password')}
                        {field('confirm_password', 'Confirm Password')}
                    </div>

                    {message && (
                        <div className={`mt-4 ${message.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                            {message.text}
                        </div>
                    )}

                    <div className="mt-6 flex items-center justify-end gap-x-6">
                        <button type="button" onClick={() => navigate(-1)} className="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
                        <button
                            onClick={handleSubmit}
                            disabled={saving}
                            type="submit"
                            className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
